import { Link } from "react-router-dom";
import { AlertTriangle, MapPin } from "lucide-react";
import { BRAND, CONTACT, DOCTOR } from "@/lib/constants";

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0A2540] text-white mt-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          <div className="md:col-span-4">
            <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-4">
              Practice
            </div>
            <h3 className="font-serif text-3xl leading-tight mb-1">
              {BRAND.name}
            </h3>
            <p className="font-sans text-sm text-white/70 leading-relaxed">
              {DOCTOR.name} — {DOCTOR.credentials}
            </p>
            <p className="font-sans text-xs text-white/50 leading-relaxed mt-4 italic">
              &ldquo;{BRAND.motto}&rdquo;
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-4">
              Clinic Location
            </div>
            <address
              data-testid="footer-address"
              className="not-italic font-sans text-sm text-white/80 leading-relaxed"
            >
              <div className="flex items-start gap-2">
                <MapPin
                  size={14}
                  strokeWidth={1.5}
                  className="mt-0.5 text-[#B08A4A] shrink-0"
                />
                <div>
                  {CONTACT.address.line1}
                  <br />
                  {CONTACT.address.line2}
                  <br />
                  {CONTACT.address.city}
                </div>
              </div>
            </address>
            <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mt-6 mb-3">
              Hours
            </div>
            <ul data-testid="footer-hours" className="space-y-1">
              {CONTACT.hours.map((h) => (
                <li
                  key={h.label}
                  className="flex justify-between font-sans text-xs text-white/80"
                >
                  <span>{h.label}</span>
                  <span className="text-white/60">{h.time}</span>
                </li>
              ))}
            </ul>
            <div
              data-testid="footer-appointment-note"
              className="mt-4 inline-block border border-[#B08A4A]/40 bg-[#B08A4A]/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A]"
            >
              {CONTACT.appointmentNote}
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-4">
              Contact
            </div>
            <ul className="space-y-2 font-sans text-sm">
              <li>
                <a
                  href={`mailto:${CONTACT.email}`}
                  data-testid="footer-email"
                  className="text-white/80 hover:text-[#B08A4A] transition-colors"
                >
                  {CONTACT.email}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
                  data-testid="footer-phone"
                  className="text-white/80 hover:text-[#B08A4A] transition-colors"
                >
                  {CONTACT.phone}
                </a>
              </li>
            </ul>
            <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mt-6 mb-3">
              Sitemap
            </div>
            <ul className="space-y-1.5 font-sans text-xs">
              <li>
                <Link to="/services" className="text-white/70 hover:text-white transition-colors">
                  → Services
                </Link>
              </li>
              <li>
                <Link to="/services/fnac-test-gwalior" className="text-white/70 hover:text-white transition-colors" data-testid="footer-fnac-link">
                  → FNAC Test in Gwalior
                </Link>
              </li>
              <li>
                <Link to="/health-notes" className="text-white/70 hover:text-white transition-colors">
                  → Health Notes
                </Link>
              </li>
              <li>
                <Link to="/research" className="text-white/70 hover:text-white transition-colors">
                  → Research Hub
                </Link>
              </li>
              <li>
                <Link to="/b2b" className="text-white/70 hover:text-white transition-colors">
                  → B2B Partnerships
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-white/70 hover:text-white transition-colors">
                  → Contact
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-4">
              Profiles
            </div>
            <ul className="space-y-2 font-sans text-sm">
              {CONTACT.socials.map((s) => (
                <li key={s.label}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-social-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-white/80 hover:text-[#B08A4A] transition-colors"
                  >
                    {s.label} →
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="rule-nav my-14 opacity-40" />

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div
            data-testid="footer-disclaimer"
            className="md:col-span-9 flex items-start gap-3 font-sans text-xs text-white/60 leading-relaxed"
          >
            <AlertTriangle
              size={14}
              strokeWidth={1.5}
              className="mt-0.5 text-[#B08A4A] shrink-0"
            />
            <p>
              <span className="text-[#B08A4A] font-mono uppercase tracking-widest-plus text-[10px] mr-2">
                Medical Disclaimer —
              </span>
              All materials on this website are provided for general
              informational purposes only. They are not a substitute for
              professional medical advice, diagnosis, or in-person clinical
              evaluation. In case of a medical emergency, do not rely on this
              website — call your local emergency services or visit the nearest
              emergency department immediately. Teleconsultation is
              supplementary to, and not a replacement for, in-person care with
              your treating physician.
            </p>
          </div>
          <p className="md:col-span-3 font-mono text-[10px] uppercase tracking-widest-plus text-white/40 md:text-right self-end">
            © {new Date().getFullYear()} {BRAND.name} · All rights reserved
          </p>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
          <h4 className="font-serif text-5xl md:text-7xl lg:text-8xl tracking-tight text-white/10 leading-none">
            {BRAND.name}
          </h4>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
